import { Field } from "@/types/Field";

/**
 * Semantic sections for the guest field pickers (filter dropdown, view column
 * picker, rules condition builder). Replaces the old flat "Guest Fields" /
 * "Additional Fields" split so a long guest field list reads as the same groups
 * the user already knows from the guest detail popup + feature areas.
 *
 * Single source of truth — every guest field picker buckets through
 * `groupGuestFields` so the three surfaces stay in sync. Group LABELS live in
 * the `fieldGroups` i18n namespace (keys === the values below); this module
 * stays i18n-free.
 */
export type GuestFieldGroupKey =
  | "core"
  | "checkIn"
  | "appointments"
  | "seating"
  | "email"
  | "website"
  | "other"
  | "additional";

/** Canonical render order. `additional` (custom fields) always last. */
export const GUEST_FIELD_GROUP_ORDER: GuestFieldGroupKey[] = [
  "core",
  "checkIn",
  "appointments",
  "seating",
  "email",
  "website",
  "other",
  "additional",
];

/**
 * Built-in guest field name → group. Custom fields (`additionalField === true`)
 * are routed to `additional` at call time and are NOT listed here. Any built-in
 * field not present falls back to `other`, so a field can never be dropped from
 * a picker by an oversight in this map.
 *
 * `core` mirrors the guest detail popup (PCreateGuest GeneralInfos +
 * ContactInfos). The feature groups mirror the activity-rollup fields in
 * FIELDS_GUEST (default-returner.ts).
 */
const GUEST_FIELD_GROUP_OF: Record<string, GuestFieldGroupKey> = {
  // core — guest popup (Stammdaten)
  guestType: "core",
  firstName: "core",
  lastName: "core",
  gender: "core",
  title: "core",
  jobTitle: "core",
  company: "core",
  email: "core",
  phone: "core",
  mobile: "core",
  address: "core",
  address2: "core",
  zipCode: "core",
  city: "core",
  country: "core",
  language: "core",
  emailOptIn: "core",
  smsOptIn: "core",
  username: "core",
  code: "core",
  loginToken: "core",
  state: "core",
  // Companion relationship is core guest data (sits next to guestType), not
  // "other": numCompanions = "Anzahl Begleitpersonen", companions = "Verknüpfung".
  numCompanions: "core",
  companions: "core",

  // `state` is the guest's acceptance funnel; `acceptanceState` is its
  // registrant/companion-table alias — both are core guest data.
  acceptanceState: "core",

  // check-in
  checkedIn: "checkIn",
  checkedInAt: "checkIn",
  // Display-only device-layout column: the door cues the Worker computes per
  // guest (see lib/devices/layout-field-options). A check-in surface, not a
  // stored field.
  cues: "checkIn",

  // appointments / slotting
  apptStatuses: "appointments",
  apptCount: "appointments",
  nextApptAt: "appointments",
  hasOpenRequest: "appointments",
  apptOfferingIds: "appointments",
  apptTreatmentIds: "appointments",
  apptLaneIds: "appointments",
  slotting: "appointments",

  // seating
  seatStatuses: "seating",
  seatCount: "seating",
  seatRoomIds: "seating",
  seating: "seating",
  // Display-only device-layout column (see lib/devices/layout-field-options).
  seatTables: "seating",

  // email
  lastEmailedAt: "email",
  lastEmailStatus: "email",
  lastEmailOpenedAt: "email",
  lastEmailClickedAt: "email",
  emailedTemplateIds: "email",
  emailedSendNames: "email",
  emailUndeliverable: "email",
  emailUndeliverableAt: "email",

  // website & portal
  portalVisitedAt: "website",
  websiteLoginAt: "website",

  // activity / other
  origin: "other",
  importId: "other",
  // Wave B provenance marker: promoted-speaker backlink (set/empty filter).
  speakerId: "other",
  // Wave L sponsor↔guests marker (set/empty filter like speakerId).
  sponsorId: "other",
  createdAt: "other",
  updatedAt: "other",
  archived: "other",
  attachments: "other",
  guestId: "other",
  checkInOutCTA: "other",
};

/** Resolve a single field to its group. */
export const guestFieldGroupOf = (field: Field): GuestFieldGroupKey =>
  field.additionalField ? "additional" : GUEST_FIELD_GROUP_OF[field.name] ?? "other";

/**
 * Bucket guest fields into the ordered semantic sections. STABLE — preserves
 * the incoming order within each bucket (callers pre-sort however they label
 * fields), and returns only NON-EMPTY groups in {@link GUEST_FIELD_GROUP_ORDER}.
 */
export function groupGuestFields(
  fields: Field[],
): { key: GuestFieldGroupKey; fields: Field[] }[] {
  return groupFieldOptions(fields, guestFieldGroupOf);
}

/**
 * Resolve a guest-field KEY STRING to its group. Only valid for keys in the
 * `getFieldKey` convention (condition-utils.ts): custom fields are
 * `additionalFields.<name>`, built-ins are bare names. Do NOT feed it bare
 * custom-field names — they would collide with built-ins (a custom field
 * literally named `checkedIn` would be mislabeled "Check-in"); surfaces that
 * key customs by bare name must bucket them into `additional` themselves.
 */
export const guestFieldGroupOfKey = (key: string): GuestFieldGroupKey =>
  key.startsWith("additionalFields.")
    ? "additional"
    : GUEST_FIELD_GROUP_OF[key] ?? "other";

/**
 * {@link groupGuestFields} generalized to arbitrary option shapes ({key,label}
 * rows, key strings, …) via a caller-supplied group resolver. Same contract:
 * stable within-bucket order, only non-empty groups, canonical group order.
 */
export function groupFieldOptions<T>(
  options: T[],
  groupOf: (option: T) => GuestFieldGroupKey,
): { key: GuestFieldGroupKey; fields: T[] }[] {
  const buckets = new Map<GuestFieldGroupKey, T[]>();
  for (const option of options) {
    const key = groupOf(option);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(option);
    else buckets.set(key, [option]);
  }
  return GUEST_FIELD_GROUP_ORDER.filter((key) => buckets.has(key)).map((key) => ({
    key,
    fields: buckets.get(key)!,
  }));
}

// ---------------------------------------------------------------------------
// Wave A — speaker/sponsor hub DataTables. Their (much shorter) built-in sets
// get their own buckets: profile / contact / meta / additional. Without an
// entity grouping the column picker (PModifyFields keys off
// `useMetaTable().type`) would dump everything into "other". Group LABELS
// live in the shared `fieldGroups` i18n namespace, same as the guest keys.
// ---------------------------------------------------------------------------

export type EntityFieldGroupKey = "profile" | "contact" | "meta" | "additional";

/** Canonical render order for the speaker/sponsor pickers. */
export const ENTITY_FIELD_GROUP_ORDER: EntityFieldGroupKey[] = [
  "profile",
  "contact",
  "meta",
  "additional",
];

const SPEAKER_FIELD_GROUP_OF: Record<string, EntityFieldGroupKey> = {
  name: "profile",
  occupation: "profile",
  imageUrl: "profile",
  socials: "profile",
  tags: "profile",
  company: "contact",
  email: "contact",
  phone: "contact",
  sortIndex: "meta",
  createdAt: "meta",
};

const SPONSOR_FIELD_GROUP_OF: Record<string, EntityFieldGroupKey> = {
  name: "profile",
  tier: "profile",
  logoUrl: "profile",
  description: "profile",
  linkUrl: "profile",
  tags: "profile",
  contactName: "contact",
  contactEmail: "contact",
  sortIndex: "meta",
  createdAt: "meta",
};

/**
 * Shared bucketing core for the entity group maps. Any built-in missing from
 * the map lands in `meta` (never dropped); custom fields go to `additional`.
 */
function groupEntityFields(
  fields: Field[],
  groupMap: Record<string, EntityFieldGroupKey>,
): { key: EntityFieldGroupKey; fields: Field[] }[] {
  const buckets = new Map<EntityFieldGroupKey, Field[]>();
  for (const field of fields) {
    const key: EntityFieldGroupKey = field.additionalField
      ? "additional"
      : groupMap[field.name] ?? "meta";
    const bucket = buckets.get(key);
    if (bucket) bucket.push(field);
    else buckets.set(key, [field]);
  }
  return ENTITY_FIELD_GROUP_ORDER.filter((key) => buckets.has(key)).map(
    (key) => ({ key, fields: buckets.get(key)! }),
  );
}

/** Bucket speaker fields into profile / contact / meta / additional. */
export function groupSpeakerFields(
  fields: Field[],
): { key: EntityFieldGroupKey; fields: Field[] }[] {
  return groupEntityFields(fields, SPEAKER_FIELD_GROUP_OF);
}

/** Bucket sponsor fields into profile / contact / meta / additional. */
export function groupSponsorFields(
  fields: Field[],
): { key: EntityFieldGroupKey; fields: Field[] }[] {
  return groupEntityFields(fields, SPONSOR_FIELD_GROUP_OF);
}

/**
 * `optionGroups` builder for OptionInput on the speaker/sponsor surfaces —
 * the entity twin of {@link buildGuestFieldOptionGroups} (same contract).
 */
export function buildEntityFieldOptionGroups(
  entity: "speaker" | "sponsor",
  fields: Field[],
  optionOf: (f: Field) => string,
  labelOfGroup: (key: EntityFieldGroupKey) => string,
): { label: string; match: (option: string) => boolean }[] {
  const grouped =
    entity === "speaker" ? groupSpeakerFields(fields) : groupSponsorFields(fields);
  return grouped.map((group) => {
    const options = new Set(group.fields.map(optionOf));
    return {
      label: labelOfGroup(group.key),
      match: (option: string) => options.has(option),
    };
  });
}

/**
 * Build the `optionGroups` prop for {@link OptionInput} from a guest field
 * list: one section per semantic group, matching options by whatever string
 * the caller feeds `OptionInput.options` (field name, `getFieldKey` key OR
 * display label — `optionOf` must mirror the caller's `options` mapping
 * exactly). `labelOfGroup` receives the group key so this module stays
 * i18n-free (pass `useTranslations("fieldGroups")`).
 *
 * Existing hand-rolled twins in ConditionRow / DeviceActionEditor are
 * migration candidates — left untouched on purpose.
 */
export function buildGuestFieldOptionGroups(
  fields: Field[],
  optionOf: (f: Field) => string,
  labelOfGroup: (key: GuestFieldGroupKey) => string,
): { label: string; match: (option: string) => boolean }[] {
  return groupGuestFields(fields).map((group) => {
    const options = new Set(group.fields.map(optionOf));
    return {
      label: labelOfGroup(group.key),
      match: (option: string) => options.has(option),
    };
  });
}
